// Client side of the shop: what the signed-in player owns (read straight from
// Supabase under RLS) and the buy flow against the edge-server's Stripe routes
// (see apps/edge-server/src/shop/shop-api.js). Prices/catalog come from the
// shared module in core, the same one the server builds Checkout Sessions from.
import { supabase } from "../supabase";
import { dsHttpOrigin } from "../../online/net";
import * as catalog from "core/src/shop/shop-catalog.js";

export type Rarity = "common" | "rare" | "epic" | "legendary";

export interface ShopItem {
  id: string;
  kind: "victory" | "flag" | "head" | "board" | "eggs";
  label: string;
  // Cosmetics/effects: egg-priced (no dollar price), rarity sets the band.
  rarity?: Rarity;
  costEggs?: number;
  // Egg packs only (the only real-money purchases):
  priceCents?: number;
  currency?: string;
  name?: string;
  eggs?: number;
  bonus?: number;
}

// The shop's shelf: everything that is sold. `exclusive` items are granted, never
// sold (the Royale's crown comes with a first win), so they are kept apart.
const ALL_ITEMS: (ShopItem & { exclusive?: string })[] = (catalog as any).ITEMS;
export const SHOP_ITEMS: ShopItem[] = ALL_ITEMS.filter((i) => !i.exclusive);
export const EXCLUSIVE_ITEMS: (ShopItem & { exclusive?: string })[] = ALL_ITEMS.filter((i) => !!i.exclusive);
export const isPurchasable: (kind: string, id: string) => boolean = (catalog as any).isPurchasable;
export const EGG_PACKS: ShopItem[] = SHOP_ITEMS.filter((i) => i.kind === "eggs");
export const RARITIES: Record<Rarity, { label: string; min: number; max: number }> = (catalog as any).RARITIES;
export const eggCost: (item: ShopItem) => number = (catalog as any).eggCost;

// The signed-in user's Golden Egg balance: sum of their ledger rows (RLS
// scopes the read to their own). null = signed out / couldn't load.
export async function fetchEggBalance(): Promise<number | null> {
  if (!supabase) return null;
  try {
    const { data, error } = (await supabase
      .from("egg_ledger")
      .select("amount")) as { data: { amount: number }[] | null; error: unknown };
    if (error || !data) return null;
    return data.reduce((sum, row) => sum + (row.amount || 0), 0);
  } catch {
    return null;
  }
}

export function priceLabel(item: ShopItem): string {
  return "$" + ((item.priceCents || 0) / 100).toFixed(2);
}

// kind -> owned item ids. Empty sets when signed out / nothing bought.
export type OwnedItems = Record<string, Set<string>>;
export function emptyOwned(): OwnedItems {
  return { victory: new Set(), flag: new Set(), head: new Set(), board: new Set() };
}

export function ownsItem(owned: OwnedItems | null, kind: string, id: string): boolean {
  return !!owned && !!owned[kind] && owned[kind].has(id);
}

// Free items are always "unlocked"; purchasables need an owned row.
export function itemUnlocked(owned: OwnedItems | null, kind: string, id: string): boolean {
  if (!isPurchasable(kind, id)) return true;
  return ownsItem(owned, kind, id);
}

// The signed-in user's purchases (RLS lets them read only their own rows).
// null = couldn't load (treat as "nothing owned", never as an error page).
export async function fetchOwnedItems(): Promise<OwnedItems | null> {
  if (!supabase) return null;
  try {
    const { data, error } = (await supabase
      .from("shop_purchases")
      .select("kind, item_id")) as { data: { kind: string; item_id: string }[] | null; error: unknown };
    if (error || !data) return null;
    const owned = emptyOwned();
    for (const row of data) {
      (owned[row.kind] = owned[row.kind] || new Set()).add(row.item_id);
    }
    return owned;
  } catch {
    return null;
  }
}

async function accessToken(): Promise<string | null> {
  if (!supabase) return null;
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

async function shopPost(path: string, body: unknown): Promise<any> {
  const token = await accessToken();
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/shop/${path}`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}

// Start a purchase: resolves to { url } (redirect there), { alreadyOwned }, or { error }.
export function startCheckout(itemId: string): Promise<any> {
  return shopPost("checkout", { itemId });
}

// After the success redirect: has the payment landed / been granted yet?
export async function fetchSessionStatus(sessionId: string): Promise<{ paid?: boolean; owned?: boolean; error?: string }> {
  const token = await accessToken();
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/shop/session-status?session_id=${encodeURIComponent(sessionId)}`, {
      headers: { authorization: `Bearer ${token}` },
    });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}

// Spend Golden Eggs on a cosmetic. Resolves to { owned, spent, balance },
// { alreadyOwned }, { error: "not_enough_eggs", balance, cost }, or { error }.
export function buyWithEggs(itemId: string): Promise<any> {
  return shopPost("buy-with-eggs", { itemId });
}

// Admin-only test grant (server re-checks the allowlist; $0 tagged rows).
export function fakeGrant(itemId: string): Promise<any> {
  return shopPost("fake-grant", { itemId });
}
